// Фильтры списков — одинаковые на всех страницах: период (Сегодня · Вчера · 7 · 14 · 30 дней · Всё время ·
// свой диапазон), выпадающий выбор со счётчиками, сортировка по колонке. Выбранный период страница помнит
// в браузере (только удобство: без хранилища всё работает с периодом по умолчанию).
import {
  MONTH_PRESETS,
  PERIOD_PRESETS,
  type Period,
  type PeriodPreset,
  parsePeriod,
  periodKey,
  periodLabel,
  periodRange,
  tashkentDay,
} from '@autoaff/schemas/period';
import { Button, Popover } from '@autoaff/ui';
import clsx from 'clsx';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarDays,
  Check,
  ChevronDown,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import {
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  type ThHTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type { Period } from '@autoaff/schemas/period';
export { periodKey, periodLabel, periodPhrase } from '@autoaff/schemas/period';

// ─── память выбора ─────────────────────────────────────────────────────
function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* приватный режим — просто не помним */
  }
}

/** Период страницы: по умолчанию — fallback, выбор запоминается для этой страницы. */
export function usePeriod(page: string, fallback: PeriodPreset): [Period, (p: Period) => void] {
  const storeKey = `period:${page}`;
  const [p, setP] = useState<Period>(() => parsePeriod(readStored(storeKey), fallback));
  const set = useCallback(
    (next: Period) => {
      setP(next);
      writeStored(storeKey, periodKey(next));
    },
    [storeKey],
  );
  return [p, set];
}

/** Выбор, который страница помнит между заходами (сортировка, вид). Неизвестное значение — fallback. */
export function useRemembered<T extends string>(
  key: string,
  fallback: T,
  allowed: readonly T[],
): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => {
    const s = readStored(key);
    return s && (allowed as readonly string[]).includes(s) ? (s as T) : fallback;
  });
  const set = useCallback(
    (next: T) => {
      setV(next);
      writeStored(key, next);
    },
    [key],
  );
  return [v, set];
}

// ─── список в выпадающей панели ────────────────────────────────────────
/** Стрелки, Home/End — по пунктам списка (кнопкам с role=option). */
function listKeys(e: KeyboardEvent<HTMLElement>) {
  const items = [...e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="option"]')];
  const i = items.indexOf(document.activeElement as HTMLButtonElement);
  const go = (n: number) => {
    e.preventDefault();
    items[(n + items.length) % items.length]?.focus();
  };
  if (e.key === 'ArrowDown') go(i + 1);
  else if (e.key === 'ArrowUp') go(i < 0 ? items.length - 1 : i - 1);
  else if (e.key === 'Home') go(0);
  else if (e.key === 'End') go(items.length - 1);
}

/** С какого края кнопки открыть панель: кнопка в правой половине экрана — панель уходит влево, и наоборот. */
function sideOf(btn: RefObject<HTMLElement | null>): 'start' | 'end' {
  const r = btn.current?.getBoundingClientRect();
  return r && r.left + r.width / 2 > window.innerWidth / 2 ? 'end' : 'start';
}

/** Фокус — на выбранный пункт, когда панель открылась. */
function useFocusSelected(open: boolean, box: RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    if (!open) return;
    const t = requestAnimationFrame(() => {
      const el =
        box.current?.querySelector<HTMLElement>('[aria-selected="true"]') ??
        box.current?.querySelector<HTMLElement>('[role="option"], input');
      el?.focus();
    });
    return () => cancelAnimationFrame(t);
  }, [open, box]);
}

// ─── период ────────────────────────────────────────────────────────────
export function PeriodPicker({
  value,
  onChange,
  months = false,
  label = 'Период',
}: {
  value: Period;
  onChange: (p: Period) => void;
  /** Добавить «Этот месяц» и «Прошлый месяц» (Финансы). */
  months?: boolean;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [side, setSide] = useState<'start' | 'end'>('end');
  const btn = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const today = tashkentDay();
  const current = periodRange(value);
  const [since, setSince] = useState(current.since ?? today);
  const [until, setUntil] = useState(current.until);
  useFocusSelected(open, list);
  // Открыли заново — в полях диапазона текущий период
  useEffect(() => {
    if (!open) return;
    const r = periodRange(value);
    setSince(r.since ?? r.until);
    setUntil(r.until);
  }, [open, value]);

  const pick = (p: Period) => {
    onChange(p);
    close();
    btn.current?.focus();
  };
  const range = parsePeriod(`${since}..${until}`, 'all');
  const rangeOk = range.kind === 'range' && since <= today && until <= today;
  const presets = months ? [...PERIOD_PRESETS, ...MONTH_PRESETS] : PERIOD_PRESETS;
  const key = periodKey(value);

  return (
    <>
      <button
        ref={btn}
        type="button"
        className="btn filter-btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${label}: ${periodLabel(value)}`}
        onClick={() => {
          setSide(sideOf(btn));
          setOpen(!open);
        }}
      >
        <CalendarDays aria-hidden />
        <span>{periodLabel(value)}</span>
        <ChevronDown aria-hidden className="filter-chev" />
      </button>
      <Popover anchor={btn} open={open} onClose={close} label={label} align={side} width={300}>
        <div ref={list} role="listbox" aria-label={label} tabIndex={-1} onKeyDown={listKeys}>
          {presets.map((o, i) => {
            const sel = o.value === key;
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={sel}
                tabIndex={sel ? 0 : -1}
                className={clsx('pick-opt', i === PERIOD_PRESETS.length && 'pick-gap')}
                onClick={() => pick({ kind: 'preset', preset: o.value })}
              >
                <span>{o.label}</span>
                {sel && <Check aria-hidden />}
              </button>
            );
          })}
        </div>
        <form
          className="pick-range"
          onSubmit={(e) => {
            e.preventDefault();
            if (rangeOk) pick(range);
          }}
        >
          <div className="pick-sec">Свой диапазон</div>
          <div className="pick-dates">
            <label>
              <span>С</span>
              <input
                className="input"
                type="date"
                value={since}
                max={until || today}
                onChange={(e) => setSince(e.target.value)}
              />
            </label>
            <label>
              <span>По</span>
              <input
                className="input"
                type="date"
                value={until}
                min={since}
                max={today}
                onChange={(e) => setUntil(e.target.value)}
              />
            </label>
          </div>
          <Button type="submit" size="sm" variant="primary" block disabled={!rangeOk}>
            Показать
          </Button>
        </form>
      </Popover>
    </>
  );
}

// ─── выпадающий выбор ──────────────────────────────────────────────────
export interface FilterOption<T extends string> {
  value: T;
  label: string;
  /** Счётчик справа (сколько строк с этим значением). */
  count?: number;
}

/**
 * Кнопка фильтра с выпадающим списком. Пока выбрано «все» (allValue) — на кнопке только название фильтра;
 * выбрано другое — его название, кнопка подсвечена. Больше 8 пунктов — поиск по списку.
 */
export function FilterSelect<T extends string>({
  label,
  value,
  onChange,
  options,
  allValue,
  icon,
  width = 280,
  showValue = false,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: Array<FilterOption<T>>;
  /** Значение «без фильтра»: при нём кнопка не подсвечена. */
  allValue?: T;
  icon?: ReactNode;
  width?: number;
  /** На кнопке — выбранный пункт и при значении по умолчанию (сортировка: «Сначала новые»). */
  showValue?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [side, setSide] = useState<'start' | 'end'>('start');
  const [q, setQ] = useState('');
  const btn = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const close = useCallback(() => {
    setOpen(false);
    setQ('');
  }, []);
  useFocusSelected(open, list);
  const selected = options.find((o) => o.value === value);
  const active = allValue !== undefined && value !== allValue;
  const searchable = options.length > 8;
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => o.label.toLowerCase().includes(s)) : options;
  }, [options, q]);

  return (
    <>
      <button
        ref={btn}
        type="button"
        className={clsx('btn filter-btn', active && 'on')}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${label}: ${selected?.label ?? '—'}`}
        onClick={() => {
          setSide(sideOf(btn));
          setOpen(!open);
        }}
      >
        {icon}
        <span>{active || showValue ? (selected?.label ?? label) : label}</span>
        <ChevronDown aria-hidden className="filter-chev" />
      </button>
      <Popover anchor={btn} open={open} onClose={close} label={label} align={side} width={width}>
        <div ref={list}>
          {searchable && (
            <label className="search-field pick-search">
              <Search aria-hidden />
              <input
                type="search"
                placeholder="Найти"
                aria-label={`Найти: ${label.toLowerCase()}`}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    list.current?.querySelector<HTMLElement>('[role="option"]')?.focus();
                  }
                }}
              />
            </label>
          )}
          <div role="listbox" aria-label={label} tabIndex={-1} onKeyDown={listKeys}>
            {shown.map((o) => {
              const sel = o.value === value;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={sel}
                  tabIndex={sel ? 0 : -1}
                  className="pick-opt"
                  onClick={() => {
                    onChange(o.value);
                    close();
                    btn.current?.focus();
                  }}
                >
                  <span>{o.label}</span>
                  <span className="pick-side">
                    {o.count !== undefined && <span className="tnum">{o.count}</span>}
                    {sel && <Check aria-hidden />}
                  </span>
                </button>
              );
            })}
            {shown.length === 0 && <p className="pick-empty">Ничего не нашлось</p>}
          </div>
        </div>
      </Popover>
    </>
  );
}

/**
 * Полоса фильтров над списком: кнопки в ряд, поиск справа (search), за ним — сортировка (end).
 * С active (сколько фильтров выбрано) на телефоне фильтры сворачиваются в одну кнопку «Фильтры (N)» рядом с
 * поиском — список начинается на первом экране; нажатие раскрывает их под строкой. На компьютере всё в ряд.
 */
export function FilterBar({
  children,
  className,
  search,
  end,
  active,
}: {
  children: ReactNode;
  className?: string;
  /** Поле поиска: на компьютере — справа, на телефоне — во всю ширину рядом с кнопкой «Фильтры». */
  search?: ReactNode;
  /** После поиска (сортировка); на телефоне — среди свёрнутых фильтров. */
  end?: ReactNode;
  /** Сколько фильтров выбрано (не «все»): задано — на телефоне фильтры в одной кнопке «Фильтры (N)». */
  active?: number;
}) {
  const [open, setOpen] = useState(false);
  if (active === undefined)
    return (
      // biome-ignore lint/a11y/useSemanticElements: fieldset ломает раскладку flex в Safari
      <div className={clsx('filters', className)} role="group" aria-label="Фильтры">
        {children}
        {search}
        {end}
      </div>
    );
  return (
    // biome-ignore lint/a11y/useSemanticElements: fieldset ломает раскладку flex в Safari
    <div className={clsx('filters filters-c', open && 'open', className)} role="group" aria-label="Фильтры">
      <div className="filters-top">
        {search}
        <button
          type="button"
          className={clsx('btn filter-btn filters-toggle', active > 0 && 'on')}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <SlidersHorizontal aria-hidden />
          <span>{active > 0 ? `Фильтры (${active})` : 'Фильтры'}</span>
          <ChevronDown aria-hidden className="filter-chev" />
        </button>
      </div>
      <div className="filters-more">
        {children}
        {end && <span className="filters-endslot">{end}</span>}
      </div>
    </div>
  );
}

/** «Сбросить» — только когда что-то выбрано. */
export function ResetFilters({ shown, onClick }: { shown: boolean; onClick: () => void }) {
  if (!shown) return null;
  return (
    <Button size="sm" variant="ghost" onClick={onClick} className="filter-reset">
      Сбросить
    </Button>
  );
}

// ─── сортировка ────────────────────────────────────────────────────────
export type SortDir = 'asc' | 'desc';
export interface SortState<K extends string> {
  key: K;
  dir: SortDir;
}

/** Сортировка по колонке: повторный клик — в обратную сторону; новая колонка — сначала «больше». */
export function useSort<K extends string>(
  initial: SortState<K>,
): [SortState<K>, (key: K, firstDir?: SortDir) => void] {
  const [s, setS] = useState(initial);
  const toggle = useCallback(
    (key: K, firstDir: SortDir = 'desc') =>
      setS((cur) =>
        cur.key === key ? { key, dir: cur.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: firstDir },
      ),
    [],
  );
  return [s, toggle];
}

/** Сравнение для сортировки: пустые значения (null) — всегда в конце; числа внутри текста — по величине (c2 раньше c10). */
export function compareBy<T>(get: (row: T) => number | string | null, dir: SortDir) {
  return (a: T, b: T): number => {
    const x = get(a);
    const y = get(b);
    if (x === null && y === null) return 0;
    if (x === null) return 1;
    if (y === null) return -1;
    const c =
      typeof x === 'number' && typeof y === 'number'
        ? x - y
        : String(x).localeCompare(String(y), 'ru', { numeric: true });
    return dir === 'asc' ? c : -c;
  };
}

/** Заголовок колонки, по которому сортируют. На телефоне шапки таблиц нет — там сортировка в полосе фильтров. */
export function SortTh<K extends string>({
  k,
  sort,
  onSort,
  children,
  firstDir = 'desc',
  className,
  ...rest
}: {
  k: K;
  sort: SortState<K>;
  onSort: (key: K, firstDir?: SortDir) => void;
  children: ReactNode;
  firstDir?: SortDir;
} & ThHTMLAttributes<HTMLTableCellElement>) {
  const on = sort.key === k;
  const Icon = on ? (sort.dir === 'desc' ? ArrowDown : ArrowUp) : ArrowUpDown;
  return (
    <th
      className={clsx(className, 'th-sort', on && 'on')}
      aria-sort={on ? (sort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
      {...rest}
    >
      <button type="button" onClick={() => onSort(k, firstDir)}>
        {children}
        <Icon aria-hidden />
      </button>
    </th>
  );
}
